import {
	ArrowUpFromDot,
	BookAlert,
	BookUser,
	Filter,
	Search,
	Shield,
	Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	type Case,
	type CaseStatus,
	MOCK_ADMIN_STATS,
	MOCK_CASES,
	MOCK_USERS,
} from '@/data/aml/mockData';
import { cn } from '@/lib/utils';
import { Routes } from '@/routes/routes';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadgeClass(status: CaseStatus): string {
	switch (status) {
		case 'open':
			return 'bg-blue-50 text-blue-700 border-blue-200';
		case 'closed':
			return 'bg-gray-100 text-gray-700 border-gray-200';
		case 'reopened':
			return 'bg-purple-50 text-purple-700 border-purple-200';
		case 'pending':
			return 'bg-yellow-50 text-yellow-700 border-yellow-200';
		case 'escalated':
			return 'bg-orange-50 text-orange-700 border-orange-200';
		default:
			return 'bg-gray-100 text-gray-600';
	}
}

function priorityBadgeClass(priority: string): string {
	const p = priority.toLowerCase();
	if (p === 'very_high' || p === 'very high')
		return 'bg-red-50 text-red-700 border-red-200';
	if (p === 'high') return 'bg-orange-50 text-orange-700 border-orange-200';
	if (p === 'medium') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
	return 'bg-green-50 text-green-700 border-green-200';
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
}

function statusLabel(status: CaseStatus): string {
	if (status === 'pending') return 'Pending Review';
	return status.charAt(0).toUpperCase() + status.slice(1);
}

// ── Case Table ────────────────────────────────────────────────────────────────

function CaseTable({
	cases,
	loading = false,
}: {
	cases: Case[];
	loading?: boolean;
}) {
	const navigate = useNavigate();

	if (loading) {
		return (
			<div className="rounded-xl border border-[#E5E5E5] bg-white p-8 text-center text-sm text-[#737373]">
				Loading cases...
			</div>
		);
	}

	if (cases.length === 0) {
		return (
			<div className="rounded-xl border border-dashed border-[#D4D4D4] bg-white p-10 text-center text-sm text-[#737373]">
				No cases found.
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-[#E5E5E5] bg-white overflow-hidden">
			<Table>
				<TableHeader>
					<TableRow className="bg-[#FAFAFA]">
						<TableHead className="text-xs font-semibold text-[#737373]">
							Case
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Status
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Disposition
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Assigned To
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Alerts
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Age
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Created
						</TableHead>
						<TableHead className="text-xs font-semibold text-[#737373]">
							Priority
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{cases.map((c) => (
						<TableRow
							key={c.id}
							className="cursor-pointer hover:bg-[#FAFAFA] transition-colors"
							onClick={() =>
								navigate(Routes.AML_CASE_DETAIL.replace(':id', c.id))
							}
						>
							<TableCell className="max-w-[260px]">
								<div className="text-sm font-semibold text-[#0A0A0A] truncate">
									{c.title}
								</div>
								<div className="text-xs text-[#737373] mt-0.5">{c.code}</div>
							</TableCell>
							<TableCell>
								<span
									className={cn(
										'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
										statusBadgeClass(c.status),
									)}
								>
									{statusLabel(c.status)}
								</span>
							</TableCell>
							<TableCell>
								{c.disposition_decision ? (
									<span
										className={cn(
											'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
											c.disposition_decision === 'true_match'
												? 'bg-red-50 text-red-700 border-red-200'
												: c.disposition_decision === 'false_match'
													? 'bg-green-50 text-green-700 border-green-200'
													: 'bg-orange-50 text-orange-700 border-orange-200',
										)}
									>
										{c.disposition_decision
											.replace('_', ' ')
											.replace(/\b\w/g, (l) => l.toUpperCase())}
									</span>
								) : (
									<span className="text-xs text-[#A3A3A3]">—</span>
								)}
							</TableCell>
							<TableCell>
								<span className="text-sm text-[#525252]">
									{c.assignee?.name ?? 'Unassigned'}
								</span>
							</TableCell>
							<TableCell>
								<span className="inline-flex items-center gap-1 text-xs text-[#737373]">
									<BookAlert className="h-3 w-3" />
									{c.alert_count}
								</span>
							</TableCell>
							<TableCell>
								<span className="text-xs text-[#737373]">{c.case_age}d</span>
							</TableCell>
							<TableCell>
								<span className="text-xs text-[#737373]">
									{formatDate(c.created_at)}
								</span>
							</TableCell>
							<TableCell>
								<span
									className={cn(
										'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
										priorityBadgeClass(c.priority),
									)}
								>
									{c.priority.toUpperCase().replace('_', ' ')}
								</span>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

// ── Filter + Table combo ──────────────────────────────────────────────────────

function CaseListContent({ cases }: { cases: Case[] }) {
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [assigneeFilter, setAssigneeFilter] = useState('all');
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 15;

	const filtered = useMemo(() => {
		let result = cases;
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(c) =>
					c.title.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
			);
		}
		if (statusFilter !== 'all')
			result = result.filter((c) => c.status === statusFilter);
		if (assigneeFilter !== 'all') {
			if (assigneeFilter === 'unassigned')
				result = result.filter((c) => !c.assignee);
			else result = result.filter((c) => c.assignee?.id === assigneeFilter);
		}
		return result;
	}, [cases, search, statusFilter, assigneeFilter]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const hasFilters = statusFilter !== 'all' || assigneeFilter !== 'all';

	return (
		<div className="space-y-4">
			<Card className="border-border/60 shadow-sm">
				<div className="flex flex-wrap items-center gap-3 p-3 bg-muted/20">
					<span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mr-2">
						<Filter className="h-3.5 w-3.5" /> Filters
					</span>
					<Select
						value={statusFilter}
						onValueChange={(v) => {
							setStatusFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[160px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Statuses" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Statuses</SelectItem>
							<SelectItem value="open">Open</SelectItem>
							<SelectItem value="closed">Closed</SelectItem>
							<SelectItem value="reopened">Reopened</SelectItem>
							<SelectItem value="pending">Pending Review</SelectItem>
							<SelectItem value="escalated">Escalated</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={assigneeFilter}
						onValueChange={(v) => {
							setAssigneeFilter(v);
							setPage(1);
						}}
					>
						<SelectTrigger className="w-[180px] h-8 text-xs bg-background">
							<SelectValue placeholder="All Agents" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Agents</SelectItem>
							<SelectItem value="unassigned">Unassigned</SelectItem>
							{MOCK_USERS.map((u) => (
								<SelectItem key={u.id} value={u.id}>
									{u.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{hasFilters && (
						<Button
							variant="ghost"
							size="sm"
							className="h-8 text-xs text-muted-foreground ml-auto"
							onClick={() => {
								setStatusFilter('all');
								setAssigneeFilter('all');
								setPage(1);
							}}
						>
							Clear Filters
						</Button>
					)}
				</div>
			</Card>

			<div className="flex items-center justify-between gap-4">
				<div className="relative w-72">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
						placeholder="Search by title or code..."
						className="pl-9 h-9 bg-background shadow-sm"
					/>
				</div>
				<span className="text-sm text-[#737373]">{filtered.length} cases</span>
			</div>

			<CaseTable cases={paginated} />

			{totalPages > 1 && (
				<div className="flex items-center justify-end gap-2 pt-2">
					<Button
						variant="outline"
						size="sm"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
					>
						Previous
					</Button>
					<span className="text-sm text-[#737373]">
						Page {page} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page >= totalPages}
						onClick={() => setPage((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
}

// ── Admin Stats ───────────────────────────────────────────────────────────────

function AdminStatCard({
	label,
	value,
	sub,
}: {
	label: string;
	value: number;
	sub?: string;
}) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<div className="text-sm text-[#737373]">{label}</div>
				<div className="text-3xl font-bold text-[#0A0A0A]">{value}</div>
			</CardHeader>
			{sub && (
				<CardContent className="pt-0">
					<p className="text-xs text-[#A3A3A3]">{sub}</p>
				</CardContent>
			)}
		</Card>
	);
}

function AdminView() {
	const stats = MOCK_ADMIN_STATS;

	const escalated = MOCK_CASES.filter((c) => c.status === 'escalated');
	const unassigned = MOCK_CASES.filter((c) => !c.assignee);
	const pending = MOCK_CASES.filter((c) => c.status === 'pending');

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<AdminStatCard label="Open Cases" value={stats.total_open_cases} />
				<AdminStatCard
					label="Unassigned"
					value={stats.unassigned_cases}
					sub="Need assignment"
				/>
				<AdminStatCard
					label="Escalated"
					value={stats.escalated_cases}
					sub="High priority"
				/>
				<AdminStatCard
					label="Pending Review"
					value={stats.pending_review}
					sub="Awaiting approval"
				/>
			</div>

			<Tabs defaultValue="escalated">
				<TabsList>
					<TabsTrigger value="escalated" className="gap-2">
						<ArrowUpFromDot className="h-3.5 w-3.5" /> Escalated
						{escalated.length > 0 && (
							<Badge
								variant="secondary"
								className="ml-1 h-5 px-1.5 py-0 text-[10px]"
							>
								{escalated.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger value="unassigned" className="gap-2">
						Unassigned
						{unassigned.length > 0 && (
							<Badge
								variant="secondary"
								className="ml-1 h-5 px-1.5 py-0 text-[10px]"
							>
								{unassigned.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger value="pending" className="gap-2">
						Pending Review
						{pending.length > 0 && (
							<Badge
								variant="secondary"
								className="ml-1 h-5 px-1.5 py-0 text-[10px]"
							>
								{pending.length}
							</Badge>
						)}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="escalated" className="mt-4">
					<CaseTable cases={escalated.slice(0, 10)} />
				</TabsContent>
				<TabsContent value="unassigned" className="mt-4">
					<CaseTable cases={unassigned.slice(0, 10)} />
				</TabsContent>
				<TabsContent value="pending" className="mt-4">
					<CaseTable cases={pending.slice(0, 10)} />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Tab = 'my_queue' | 'all_cases' | 'admin_view';

const MY_QUEUE_USERS = ['user_3', 'user_4', 'user_5'];
const myQueueCases = MOCK_CASES.filter(
	(c) => c.assignee && MY_QUEUE_USERS.includes(c.assignee.id),
);

export default function CaseManagerPage() {
	const [activeTab, setActiveTab] = useState<Tab>('my_queue');

	return (
		<div className="min-h-screen bg-background">
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">
							Case Manager
						</h1>
						<p className="text-sm text-[#737373] mt-0.5">
							Manage AML investigation cases and workflows
						</p>
					</div>
					<Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
						<TabsList className="grid grid-cols-3">
							<TabsTrigger value="my_queue" className="flex items-center gap-2">
								<BookUser className="h-3.5 w-3.5" /> My Queue
							</TabsTrigger>
							<TabsTrigger
								value="all_cases"
								className="flex items-center gap-2"
							>
								<Users className="h-3.5 w-3.5" /> All Cases
							</TabsTrigger>
							<TabsTrigger
								value="admin_view"
								className="flex items-center gap-2"
							>
								<Shield className="h-3.5 w-3.5" /> Admin View
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			</div>

			<div className="p-6">
				{activeTab === 'my_queue' && <CaseListContent cases={myQueueCases} />}
				{activeTab === 'all_cases' && <CaseListContent cases={MOCK_CASES} />}
				{activeTab === 'admin_view' && <AdminView />}
			</div>
		</div>
	);
}
